import { NumberField as BaseNumberField } from '@connected/form-fields/components/number';
import { SingleCheckOptionItem } from '@connected/form-fields/components/option';
import { InputGroup, InputGroupInput } from '@connected/ui/components/input-group';
import { Popover, PopoverContent, PopoverTrigger } from '@connected/ui/components/popover';
import { ScrollArea } from '@connected/ui/components/scroll-area';
import { cn } from '@connected/ui/lib/utils';
import { PopoverAnchor } from '@radix-ui/react-popover';
import { ArrowDown, ArrowUp, ChevronDownIcon } from 'lucide-react';
import { Fragment, useCallback, useEffect, useId, useRef, useState } from 'react';

import type { Option, OptionValue } from '../../types';
import type { FormTableFieldApi } from '../types';

import { NumberField } from './number-field';
import { TextField } from './text-field';

// Typeahead reset delay in ms
const TYPEAHEAD_RESET_DELAY = 500;

export type InputWithDropdownProps<TValue extends OptionValue> = {
    /** Field API from FormTable cell renderer */
    field: FormTableFieldApi<TValue | null>;
    /** Options to display in the dropdown */
    options: Array<Option<TValue>>;
    /** Input placeholder text */
    placeholder?: string;
    /** Whether the field is disabled */
    disabled?: boolean;
    /** Additional class name */
    className?: string;
    /** Maximum items before scrolling in dropdown */
    maxItemsBeforeScroll?: number;
    /** Item height for scroll calculation */
    itemHeight?: number;
    /** Input type - 'text' for string values, 'number' for numeric values */
    inputType?: 'text' | 'number';
    /** Called when field value changes (for custom side effects like API patching) */
    onValueChange?: (value: TValue | null) => void;
};

/**
 * InputWithDropdown - A text/number input with dropdown for predefined options.
 *
 * Allows users to either type a custom value or select from predefined options.
 * Integrates with FormTable's field API for form-connected input.
 */
export function InputWithDropdown<TValue extends OptionValue>({
    field,
    options,
    placeholder,
    disabled = false,
    className,
    maxItemsBeforeScroll = 8,
    itemHeight = 40,
    inputType = 'text',
    onValueChange,
}: InputWithDropdownProps<TValue>) {
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState<number>(-1);
    const [typeaheadBuffer, setTypeaheadBuffer] = useState('');
    const typeaheadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Generate stable IDs for ARIA
    const instanceId = useId();
    const inputId = `input-with-dropdown-input-${instanceId}`;
    const listboxId = `input-with-dropdown-listbox-${instanceId}`;
    const getOptionId = (index: number) => `input-with-dropdown-option-${instanceId}-${index}`;

    // Refs for option elements (for scroll-into-view)
    const optionRefs = useRef<(HTMLDivElement | null)[]>([]);

    const currentValue = field.state.value;

    // Find the index of the currently selected value
    const selectedIndex = options.findIndex(opt => opt.value === currentValue);

    // Track previous open state to detect open/close transitions
    const prevOpenRef = useRef(open);
    useEffect(() => {
        const wasOpen = prevOpenRef.current;
        prevOpenRef.current = open;

        // Only update state on open/close transitions
        if (open && !wasOpen) {
            // Initialize to selected item or first item when opening
            setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
        }
        else if (!open && wasOpen) {
            // Reset when closing
            setActiveIndex(-1);
            setTypeaheadBuffer('');
        }
    }, [open, selectedIndex]);

    // Scroll active option into view when activeIndex changes
    useEffect(() => {
        if (open && activeIndex >= 0 && optionRefs.current[activeIndex]) {
            optionRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
        }
    }, [open, activeIndex]);

    // Handle option select (extracted for reuse)
    const handleOptionSelect = useCallback((value: TValue) => {
        field.handleChange(value);
        setOpen(false);
        // Trigger side effect immediately on option select
        if (onValueChange) {
            onValueChange(value);
        }
    }, [field, onValueChange]);

    // Typeahead: find option starting with typed characters
    const handleTypeahead = useCallback((char: string) => {
        // Clear existing timeout
        if (typeaheadTimeoutRef.current) {
            clearTimeout(typeaheadTimeoutRef.current);
        }

        const newBuffer = typeaheadBuffer + char.toLowerCase();
        setTypeaheadBuffer(newBuffer);

        // Find matching option
        const matchIndex = options.findIndex(opt =>
            String(opt.label).toLowerCase().startsWith(newBuffer),
        );
        if (matchIndex >= 0) {
            setActiveIndex(matchIndex);
        }

        // Reset buffer after delay
        typeaheadTimeoutRef.current = setTimeout(() => {
            setTypeaheadBuffer('');
        }, TYPEAHEAD_RESET_DELAY);
    }, [typeaheadBuffer, options]);

    // Keyboard handler for combobox navigation
    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (disabled)
            return;

        const optionCount = options.length;
        if (optionCount === 0)
            return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                if (!open) {
                    setOpen(true);
                }
                else {
                    setActiveIndex(prev => (prev + 1) % optionCount);
                }
                break;

            case 'ArrowUp':
                e.preventDefault();
                if (!open) {
                    setOpen(true);
                }
                else {
                    setActiveIndex(prev => (prev - 1 + optionCount) % optionCount);
                }
                break;

            case 'Home':
                if (open) {
                    e.preventDefault();
                    setActiveIndex(0);
                }
                break;

            case 'End':
                if (open) {
                    e.preventDefault();
                    setActiveIndex(optionCount - 1);
                }
                break;

            case 'PageUp':
                if (open) {
                    e.preventDefault();
                    setActiveIndex(prev => Math.max(0, prev - 5));
                }
                break;

            case 'PageDown':
                if (open) {
                    e.preventDefault();
                    setActiveIndex(prev => Math.min(optionCount - 1, prev + 5));
                }
                break;

            case 'Enter':
                e.preventDefault(); // Prevent form submission
                if (open && activeIndex >= 0 && activeIndex < optionCount) {
                    handleOptionSelect(options[activeIndex].value);
                }
                break;

            case 'Escape':
                if (open) {
                    e.preventDefault();
                    setOpen(false);
                    // Return focus to input
                    document.getElementById(inputId)?.focus();
                }
                break;

            case 'Tab':
                // Allow tab to naturally move focus; close popover
                if (open) {
                    setOpen(false);
                }
                break;

            default:
                // Typeahead for printable characters
                if (open && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
                    handleTypeahead(e.key);
                }
                break;
        }
    }, [disabled, options, open, activeIndex, handleTypeahead, handleOptionSelect, inputId]);

    const handleBlur = () => {
        field.handleBlur();
        // Trigger side effect (e.g., API patch) on blur
        if (onValueChange) {
            onValueChange(field.state.value as TValue | null);
        }
    };

    const Wrapper = options.length > maxItemsBeforeScroll ? ScrollArea : Fragment;

    // Ensure optionRefs array has correct length
    useEffect(() => {
        optionRefs.current = optionRefs.current.slice(0, options.length);
    }, [options.length]);

    const optionsList = (
        <Wrapper
            {...(Wrapper === ScrollArea
                ? {
                        style: {
                            height: `${maxItemsBeforeScroll * itemHeight}px`,
                        },
                        className: 'scroll-area-nested',
                    }
                : {})}
        >
            <div
                role="listbox"
                id={listboxId}
                aria-label="Options"
                className="flex flex-col gap-1"
            >
                {options.map((option, index) => {
                    const isActive = index === activeIndex;
                    const isSelected = option.value === currentValue;
                    return (
                        <div
                            key={`${String(option.value)}-${option.label}`}
                            ref={(el) => { optionRefs.current[index] = el; }}
                            id={getOptionId(index)}
                            role="option"
                            aria-selected={isSelected}
                            data-active={isActive}
                            className={cn(
                                'rounded-md',
                                isActive && 'ring-2 ring-ring ring-offset-1',
                            )}
                        >
                            <SingleCheckOptionItem
                                option={option}
                                selected={isSelected}
                                onSelect={() => handleOptionSelect(option.value)}
                                name={field.name}
                                color={option.color}
                                disabled={disabled}
                            />
                        </div>
                    );
                })}
            </div>
        </Wrapper>
    );

    // If no options, just render a simple input using composed components
    if (options.length === 0) {
        if (inputType === 'number') {
            return (
                <NumberField
                    field={field as FormTableFieldApi<number | null | undefined>}
                    placeholder={placeholder}
                    disabled={disabled}
                    className={className}
                    onValueChange={onValueChange as ((value: number | null) => void) | undefined}
                />
            );
        }
        return (
            <TextField
                field={field as FormTableFieldApi<string | null | undefined>}
                placeholder={placeholder}
                disabled={disabled}
                className={className}
                onValueChange={onValueChange as ((value: string | null) => void) | undefined}
            />
        );
    }

    // Shared dropdown content (PopoverContent)
    const popoverContent = (
        <PopoverContent
            align="start"
            style={{ width: 'var(--radix-popover-trigger-width)' }}
            className="flex flex-col p-1"
            onOpenAutoFocus={e => e.preventDefault()}
            onCloseAutoFocus={(e) => {
                // Redirect focus to input instead of trigger button
                e.preventDefault();
                document.getElementById(inputId)?.focus();
            }}
            onWheel={e => e.stopPropagation()}
            onTouchMove={e => e.stopPropagation()}
        >
            {Wrapper === ScrollArea && (
                <ArrowUp size={14} className="mx-auto mb-1 text-text-quaternary" />
            )}
            {optionsList}
            {Wrapper === ScrollArea && (
                <ArrowDown size={14} className="mx-auto mt-1 text-text-quaternary" />
            )}
        </PopoverContent>
    );

    // Shared trigger button with ARIA attributes
    const triggerButton = (
        <InputGroup.Addon align="inline-end">
            <PopoverTrigger asChild>
                <InputGroup.Button
                    disabled={disabled}
                    aria-label="Toggle options"
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    tabIndex={-1} // Input handles keyboard; button is mouse-only
                >
                    <ChevronDownIcon
                        className={cn('size-4 transition-transform', {
                            'rotate-180': open,
                        })}
                    />
                </InputGroup.Button>
            </PopoverTrigger>
        </InputGroup.Addon>
    );

    // For number input, use BaseNumberField with customInput for proper number handling
    if (inputType === 'number') {
        // ARIA attributes for combobox pattern (passed through via ...props spread)
        const ariaProps = {
            'id': inputId,
            'role': 'combobox' as const,
            'aria-expanded': open,
            'aria-haspopup': 'listbox' as const,
            'aria-controls': open ? listboxId : undefined,
            'aria-activedescendant': open && activeIndex >= 0 ? getOptionId(activeIndex) : undefined,
            'aria-autocomplete': 'list' as const,
            'onKeyDown': handleKeyDown,
        };

        return (
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverAnchor asChild>
                    <InputGroup className={className} data-disabled={disabled}>
                        <BaseNumberField
                            name={field.name}
                            value={currentValue as number | null}
                            onChange={(value) => {
                                field.handleChange(value as TValue);
                            }}
                            onBlur={handleBlur}
                            placeholder={placeholder}
                            disabled={disabled}
                            customInput={InputGroupInput}
                            className="flex-1 rounded-none border-0 bg-transparent pr-0 shadow-none focus-visible:ring-0 dark:bg-transparent"
                            {...ariaProps}
                        />
                        {triggerButton}
                    </InputGroup>
                </PopoverAnchor>
                {popoverContent}
            </Popover>
        );
    }

    // For text input, use InputGroup.Input with combobox ARIA pattern
    const displayValue = currentValue != null ? String(currentValue) : '';

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverAnchor asChild>
                <InputGroup className={className} data-disabled={disabled}>
                    <InputGroup.Input
                        id={inputId}
                        name={field.name}
                        value={displayValue}
                        onChange={(e) => {
                            const value = e.target.value || null;
                            field.handleChange(value as TValue);
                        }}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyDown}
                        placeholder={placeholder}
                        disabled={disabled}
                        className="pr-0"
                        role="combobox"
                        aria-expanded={open}
                        aria-haspopup="listbox"
                        aria-controls={open ? listboxId : undefined}
                        aria-activedescendant={open && activeIndex >= 0 ? getOptionId(activeIndex) : undefined}
                        aria-autocomplete="list"
                    />
                    {triggerButton}
                </InputGroup>
            </PopoverAnchor>
            {popoverContent}
        </Popover>
    );
}
